import * as React from 'react';
import './DealOfTheDay.css';
import './Share.css';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import Slide from '@mui/material/Slide';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BoltIcon from '@mui/icons-material/Bolt';
import SellIcon from '@mui/icons-material/Sell';
import VerifiedIcon from '@mui/icons-material/Verified';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { sendData, truncateAtWord, formatDecimal, copyToClipboard } from '../functions/Utils.js';
import useAnalyticsEventTracker from './useAnalyticsEventTracker';

/* Intensity 1 (lowest) of the three featured-deal variants. Unlike the
   Featured / Flash banners (curated cut-out heroes), this is the real
   "top deal of the day" — the active deal whose product earned the most
   affiliate commission today, picked server-side and passed in as `deal`
   by App. Its photo is shown catalog-style on a rounded near-white tile (a
   real feed image, not a floating cut-out). App also excludes it from the
   carousel so it isn't shown twice. */

const TITLE_MAX_CHARS = 80;

// Amazon thumbnails come back small (e.g. `_SL160_`); the tile renders up to
// 200px, so bump the size token for a sharper image at no extra request cost.
const upscaleAmazonImage = (url) =>
  (typeof url === 'string' ? url.replace(/_SL\d+_/, '_SL500_') : url);

// Own hash (distinct from the carousel's #openFeaturedPromo) so the two promo
// dialogs never open at once.
const PROMO_HASH = '#openDotdPromo';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function DealOfTheDay({ deal, isHidden }) {
  const { t } = useTranslation();
  const fr = i18n.language === 'fr';
  const gaEventTracker = useAnalyticsEventTracker('DealOfTheDay');
  const isFacebookInAppBrowser = navigator.userAgent.match(/FBAN|FBAV|FB_IAB/i);
  const [openPromo, setOpenPromo] = React.useState(window.location.hash === PROMO_HASH);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    const onHashChange = () => setOpenPromo(window.location.hash === PROMO_HASH);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  if (isHidden || !deal) return null;

  const title = fr ? deal.titleFr : deal.titleEn;
  const displayTitle = truncateAtWord(title, TITLE_MAX_CHARS);
  const discountPct = deal.totalDiscountPCT > 0 ? deal.totalDiscountPCT : deal.discount;

  // Deal-info breakdown lines (icon + label), mirroring the deal-list card
  // lines. Capped at 4 rows — if a deal has more, the price drop (least
  // important) is dropped first, matching the deal-card priority.
  const allInfo = [];
  if (deal.priceDropBool) {
    allInfo.push({ key: 'priceDrop', droppable: true, node: <span key="priceDrop" className="deal-of-the-day-info-line"><TrendingDownIcon className="deal-of-the-day-info-icon" fontSize="inherit"/>{t("Deal_Card_Price_Drop")} ~{deal.priceDrop}$</span> });
  }
  if (deal.discount !== 0 && deal.isLightning) {
    allInfo.push({ key: 'lightning', node: <span key="lightning" className="deal-of-the-day-info-line"><BoltIcon className="deal-of-the-day-info-icon" fontSize="inherit"/>{t("Deal_Card_Lightning_deal", { "0": deal.discount })} {deal.discount}%</span> });
  }
  if (deal.discount !== 0 && !deal.isLightning && !deal.isPrimeExclusive) {
    allInfo.push({ key: 'discount', node: <span key="discount" className="deal-of-the-day-info-line"><SellIcon className="deal-of-the-day-info-icon" fontSize="inherit"/>{t("Deal_Card_Discount")} {deal.discount}%</span> });
  }
  if (deal.discount !== 0 && deal.isPrimeExclusive) {
    allInfo.push({ key: 'prime', node: <span key="prime" className="deal-of-the-day-info-line"><VerifiedIcon className="deal-of-the-day-info-icon" fontSize="inherit"/>{t("Deal_Card_Prime_Exclusive")} {deal.discount}%</span> });
  }
  if (deal.promoCode) {
    allInfo.push({ key: 'promoCode', node: <span key="promoCode" className="deal-of-the-day-info-line"><EditNoteIcon className="deal-of-the-day-info-icon" fontSize="inherit"/>{t("Deal_Card_Promo_Code")} {deal.promoCodeDiscount}{deal.promoCodeType}</span> });
  }
  if (deal.couponBool) {
    allInfo.push({ key: 'coupon', node: <span key="coupon" className="deal-of-the-day-info-line"><ConfirmationNumberIcon className="deal-of-the-day-info-icon" fontSize="inherit"/>{t("Deal_Card_Coupon")} {formatDecimal(deal.coupon)}{deal.couponType}</span> });
  }
  if (deal.checkoutDiscountBool) {
    allInfo.push({ key: 'checkout', node: <span key="checkout" className="deal-of-the-day-info-line"><ShoppingCartIcon className="deal-of-the-day-info-icon" fontSize="inherit"/>{t("Deal_Card_Checkout_Discount")} {deal.checkoutDiscount}{deal.checkoutType}</span> });
  }
  const infoLines = allInfo.length > 4 ? allInfo.filter((l) => !l.droppable).slice(0, 4) : allInfo;

  const openDeal = () => {
    if (isFacebookInAppBrowser) sessionStorage.setItem('dealId', deal.id);
    gaEventTracker('ClickDealOfTheDay', '', deal.id);
    // Deals carrying a promo code open the copy-able code popup first (same as
    // the carousel / deal list); otherwise go straight to the merchant.
    if (!deal.promoCode) {
      window.open(deal.dealUrl, '_blank', 'noopener,noreferrer');
      sendData(deal.id, 'Click');
      return;
    }
    setCopied(false);
    window.location.hash = PROMO_HASH;
  };

  const handleClose = () => {
    window.history.back();
    if (!isFacebookInAppBrowser) setCopied(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDeal();
    }
  };

  return (
    <section className="deal-of-the-day" aria-label={fr ? "Meilleur deal du jour" : "Top deal of the day"}>
      <div
        className="deal-of-the-day-card"
        onClick={openDeal}
        onKeyDown={onKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className="deal-of-the-day-image-wrap">
          {/* The "deal of the day" is a real organic item from the feed, so its
              photo sits on a rounded near-white tile like the deal-list / carousel
              items — not a floating cut-out like the Featured / Flash banners. */}
          <div className="deal-of-the-day-image-frame">
            <img
              src={deal.imageUrl ? upscaleAmazonImage(deal.imageUrl) : '/EmptyImage.jpg'}
              alt={title}
              className="deal-of-the-day-image"
              onError={(e) => {
                if (!e.currentTarget.src.endsWith('/EmptyImage.jpg')) {
                  e.currentTarget.src = '/EmptyImage.jpg';
                }
              }}
            />
          </div>
        </div>

        <div className="deal-of-the-day-content">
          <span className="deal-of-the-day-eyebrow">
            {t('DealOfTheDay_Eyebrow', {
              defaultValue: fr ? "Meilleur deal du jour" : "Top deal of the day",
            })}
          </span>
          {deal.brand && <div className="deal-of-the-day-brand">{deal.brand}</div>}
          <h2 className="deal-of-the-day-title">{displayTitle}</h2>
          {/* Available deal infos — promo breakdown (icon + label), up to 4 rows. */}
          <div className="deal-of-the-day-info">
            {infoLines.map((l) => l.node)}
          </div>
          {deal.priceFinal > 0 && (
            <div className="deal-of-the-day-pricing">
              <span className="deal-of-the-day-price">${deal.priceFinal.toFixed(2)}</span>
              {deal.priceRegular > deal.priceFinal && (
                <span className="deal-of-the-day-original">${deal.priceRegular.toFixed(2)}</span>
              )}
              {discountPct > 0 && (
                <span className="deal-of-the-day-discount">−{discountPct}%</span>
              )}
            </div>
          )}
          <button
            type="button"
            className="deal-of-the-day-cta"
            onClick={(e) => { e.stopPropagation(); openDeal(); }}
          >
            <span className="deal-of-the-day-cta-label">
              {t('DealOfTheDay_CTA', {
                defaultValue: fr ? "Voir le deal" : "View deal",
              })}
            </span>
            <ChevronRightIcon className="deal-of-the-day-cta-icon" aria-hidden="true" />
          </button>
        </div>
      </div>

      <Dialog
        className="DialogBox titleColor1"
        sx={{ m: 0, p: 0 }}
        open={openPromo}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <DialogTitle sx={{ m: 0, p: 2, fontSize: '35px' }}>{t('Promocode_Popup_Title')}</DialogTitle>
        <IconButton
          aria-label="fermer"
          onClick={handleClose}
          size="large"
          sx={{ position: 'absolute', right: 0, top: 12, color: '#0d1b36' }}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
        <DialogContent className="DialogBoxContent">
          <DialogContentText id="detailDialog">
            <div>
              <span>{t('Promocode_Popup_Content')}</span>
              <br />
              <br />
              <button
                className={copied ? 'codePromo copied' : 'codePromo'}
                onClick={() => {
                  copyToClipboard(deal.promoCode).then((ok) => { if (ok) setCopied(true); });
                }}
              >
                {deal.promoCode}
                <br />
                {copied && <span>{t('Promocode_Popup_Clicked_Code_Msg')}</span>}
              </button>
              <br />
              <span className="codePromoUnder">{t('Promocode_Popup_Click_Code_Msg')}</span>
            </div>
            <br />
            <Button
              className="button-color1"
              onClick={() => {
                handleClose();
                window.open(deal.dealUrl, '_blank', 'noopener,noreferrer');
                sendData(deal.id, 'Click');
                gaEventTracker('Click', '', deal.id);
              }}
            >
              {t('Promocode_Popup_Button')}
            </Button>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </section>
  );
}
